// src/builder/programClipToEntry.js
// ============================================================================
// 1B-2 — PERMANENT Program Builder transform.
//
// Converts ONE source Program-clip asset into ONE frozen ProgramEntry v1
// occurrence. Every call mints a fresh entryId, so adding the same clip N times
// yields N independent occurrences (same assetId, distinct entryIds) — the
// circuit model.
//
// Contract (ratified, evidence-backed):
//   ProgramEntry = { entryId, order, assetId, key, exerciseName, durationMs, spriteUrl }
//
// Field sources (from real runtime evidence — NOT the old flat sample):
//   assetId      <- asset.id
//   key          <- asset.key                       (verbatim; env prefix like "staging/" included)
//   exerciseName <- asset.exerciseConfig.exerciseName   (NESTED, not top-level)
//   durationMs   <- asset.durationMs                (top-level clip length incl. authored rest;
//                                                    NOT asset.exerciseConfig.duration — that's a
//                                                    prescription value and is sometimes null)
//   spriteUrl    <- asset.spriteUrl                 (raw public URL; shows burned-in prescription)
//
// Prescription (sets/reps/rxMode/duration/categories) is intentionally NOT
// carried — it is burned into the clip video and has no downstream consumer.
//
// Design boundaries (per review):
//   - Defensive optional access, but NO fallback/default values. A broken asset
//     must fail VISIBLY, not be normalized into apparently-valid data.
//   - Validates the ACTUAL contract per field (durationMs must be a finite
//     positive number, not merely truthy).
//   - Does NOT inspect composition state; `order` is caller-supplied.
//   - Does NOT renumber. Re-numbering after reorder/remove is composition-layer
//     logic (see normalizeOrder), kept out of this permanent transform.
// ============================================================================

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
const isPositiveFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;

/**
 * Transform one Program-clip asset into one frozen ProgramEntry occurrence.
 * @param {object} asset  A raw Program-clip asset (as returned by useProgramAssets()).
 * @param {number} order  Caller-supplied position in the composition.
 * @returns {object} ProgramEntry (7 fields).
 * @throws {Error} if any required source field is missing/invalid — nothing is
 *                 returned, so the caller must not add an occurrence.
 */
export function programClipToEntry(asset, order) {
  if (!asset || typeof asset !== 'object') {
    throw new Error('programClipToEntry: asset is missing or not an object');
  }
  if (!(typeof order === 'number' && Number.isInteger(order) && order >= 0)) {
    throw new Error(`programClipToEntry: order must be a non-negative integer (got ${JSON.stringify(order)})`);
  }

  const assetId = asset.id;
  const key = asset.key;
  const exerciseName = asset.exerciseConfig?.exerciseName;
  const durationMs = asset.durationMs;
  const spriteUrl = asset.spriteUrl;

  // Validate the ACTUAL contract, per field. Fail loudly, name the field.
  if (!isNonEmptyString(assetId)) {
    throw new Error('programClipToEntry: missing/invalid required Program field "assetId" (asset.id)');
  }
  if (!isNonEmptyString(key)) {
    throw new Error('programClipToEntry: missing/invalid required Program field "key" (asset.key)');
  }
  if (!isNonEmptyString(exerciseName)) {
    throw new Error('programClipToEntry: missing/invalid required Program field "exerciseName" (asset.exerciseConfig.exerciseName)');
  }
  if (!isPositiveFiniteNumber(durationMs)) {
    throw new Error(`programClipToEntry: missing/invalid required Program field "durationMs" (must be a finite positive number, got ${JSON.stringify(durationMs)})`);
  }
  if (!isNonEmptyString(spriteUrl)) {
    throw new Error('programClipToEntry: missing/invalid required Program field "spriteUrl" (asset.spriteUrl)');
  }

  return {
    entryId: crypto.randomUUID(),   // fresh per call → independent occurrence identity
    order,                          // caller-supplied; transform never inspects composition
    assetId,
    key,
    exerciseName,
    durationMs,
    spriteUrl,
  };
}

/**
 * Composition-layer helper (NOT part of the transform): remap an array of
 * entries to sequential order 0,1,2,... after reorder/remove. Preserves array
 * order; only rewrites the `order` field. entryId/identity untouched.
 * @param {object[]} entries
 * @returns {object[]} new array with normalized `order`
 */
export function normalizeOrder(entries) {
  return entries.map((e, i) => (e.order === i ? e : { ...e, order: i }));
}